/**
 * Leave Request Sheet (Spoor 2, Verlof MVP — Story 1.3, UX V2)
 *
 * Custom modal (this project has no shadcn/ui — see
 * `features/employees/components/UitDienstDialog.tsx` for the established
 * overlay + Escape-to-close pattern this follows).
 *
 * Live uren-voorrekening (AC 1/AC 3) runs client-side via the SAME shared
 * `calculateLeaveHours` resolver the backend uses (AD-5: one resolver,
 * imported everywhere) — purely for immediate UI feedback. The server is
 * authoritative and recalculates independently on submit (AD-2).
 */
import { useEffect, useMemo, useState } from 'react';
import {
  calculateLeaveHours,
  findScheduleEntryForDate,
  urenUitTijdvak,
  valtOnderNieuweVerlofregels,
  soortTeltMeeInMaandruimte,
  VERLOFSOORTEN,
  // Story 10.1/10.2/10.4: de rechtentabel en de twee gedeelde afleidingen. Het
  // handboek staat in `packages/shared` en wordt hier gelezen, nooit nagemaakt
  // (AD-38, KE-9); de uren en de werkdagen komen uit dezelfde functies die de
  // server gebruikt (KE-4).
  BIJZONDER_VERLOF_GEBEURTENISSEN,
  BIJZONDER_VERLOF_GEBEURTENIS_WAARDEN,
  bijzondereGebeurtenisRegel,
  isBijzonderVerlof,
  werkbareDagenInPeriode,
  einddatumNaWerkdagen,
  type BijzondereGebeurtenis,
  type DayPart,
  type OwnLeaveOverview,
  type Verlofsoort,
} from '@functieloonhuis/shared';
import { useAuth } from '../auth';
import { useEmployeeSchedule, useHolidays } from '../werkrooster/hooks/useWorkSchedules';
import { usePauzeVenster } from '../settings/useSettings';
import { useMijnVerlof } from './hooks/useMijnVerlof';
import { useSubmitLeaveRequest } from './hooks/useSubmitLeaveRequest';
import { useVerlofsoortIngangsdatum } from './hooks/useVerlofsoortIngangsdatum';
import { useMaandruimte } from './hooks/useMaandruimte';
import {
  MAANDRUIMTE_NIET_OP_TE_HALEN,
  MAANDRUIMTE_TOELICHTING_DEELTIJD,
  MAANDRUIMTE_TOELICHTING_GRENS,
  maandruimteZin,
} from './maandruimteTeksten';
import { LEAVE_TYPE_NAMES } from '../nmbrs/components/NmbrsCalendarLegend';
import {
  VERLOFSOORT_VRAAG,
  VERLOFSOORT_FOUT_GEEN_KEUZE,
  VERLOFSOORT_OPGERUIMD,
  VERLOFSOORT_KEUZE_TEKSTEN,
  VERLOFSOORT_VOORWAARDEN,
} from './verlofsoortKeuzeTeksten';
import {
  AANVULLENDE_BEPALINGEN,
  ALTIJD_LANGS_HR,
  GEBEURTENIS_VRAAG,
  SAMENLOOP_UITLEG,
  VINKJE_BEGRAFENISDATUM,
  VINKJE_UITLEG,
  VOORSTEL_TOELICHTING,
  VOORVULLING_MISLUKT,
  uitkomstZin,
} from './bijzonderVerlofTeksten';
// Story 8.1, AC-4 (KE-11): dezelfde ene lezing van `leidinggevendeBeslist` als
// de statusregel gebruikt — niet een tweede kopie in dit bestand.
import { leidinggevendeBeslistNiet } from './HrStapStatus';
import { ApiRequestError } from '../../lib/api';

/**
 * Story 1.3 (UX-DR14, microcopy-tabel): NL-getalnotatie voor uren — hele
 * getallen zonder decimaal (`1`), anders één decimaal met komma (`1,5`).
 * Kleine, lokale kopie van `aanwezigheidVisuals.ts`'s `formatNlUren` (niet
 * geëxporteerd vanuit die feature-map) — hier bewust niet gedeeld via
 * `@functieloonhuis/shared` omdat het puur presentatie is, geen bedrijfsregel
 * (AD-5 gaat over de urenberekening zelf, niet over de weergave ervan).
 */
function formatNlUren(uren: number): string {
  const rounded = Math.round(uren * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1).replace('.', ',');
}

/**
 * AD-2 saldo-reservering (AC 3): het live saldo dat het formulier toont is
 * het Nmbrs-saldo minus de uren van eigen aanvragen (in_afwachting of
 * goedgekeurd) die nog niet als synced in Nmbrs staan -- reuses the exact
 * data `useMijnVerlof` (Story 1.2) already fetches, no duplicate endpoint.
 */
function reservedRemaining(overview: OwnLeaveOverview | undefined, leaveType: number): number | null {
  if (!overview) return null;
  const balance = overview.balances.find((b) => b.type === LEAVE_TYPE_NAMES[leaveType]);
  // `remaining === null` betekent dat het saldo niet op te halen was. Dan is er
  // geen getal om van af te trekken; `null` gaat door naar de UI, die dat al
  // als "onbekend" toont in plaats van als 0.
  if (!balance || balance.remaining === null) return null;
  const reserved = overview.history
    .filter((h) => h.leaveType === leaveType)
    .filter((h) => h.requestStatus === 'in_afwachting' || h.requestStatus === 'goedgekeurd')
    .filter((h) => h.syncStatus !== 'synced')
    .reduce((sum, h) => sum + h.hours, 0);
  return balance.remaining - reserved;
}

interface LeaveRequestSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmitted?: () => void;
  /**
   * Aanvragen NAMENS deze medewerker in plaats van voor jezelf. Zo'n
   * aanvraag wordt direct goedgekeurd en meteen in Nmbrs geboekt — daarom
   * zit er een bevestigingsstap voor.
   *
   * Wanneer dit gezet is, komt álles van deze medewerker: het rooster voor
   * de urenberekening, de naam in de kop. Het saldo van de ingelogde
   * gebruiker wordt niet getoond én niet opgehaald; dat is andermans saldo
   * onder de verkeerde naam.
   */
  forEmployee?: { id: number; name: string };
  /**
   * Story 7.5: de gekozen verlofsoort. Bepaalt of er een HR-stap op deze
   * aanvraag zit, en daarmee of `hr`/`admin` hem in ditzelfde scherm zelf
   * beoordeelt (AD-39). Afwezig betekent gewoon verlof — de toestand van
   * alles wat aan dit werk voorafging (FR-19). De keuze zelf komt uit story
   * 10.1; dit scherm toont alleen wat er uit volgt.
   */
  verlofsoort?: Verlofsoort;
}

/**
 * Story 7.5, AC-3 (§10.5, bindend onder KE-7) — de twee vastgestelde zinnen.
 * Woord voor woord, op één plek, en hier niet ter plekke bedacht. De derde
 * variant (medisch bezoek boven de maandruimte) is nergens woordelijk
 * vastgesteld en staat daarom NIET in deze tabel: dan verschijnt de
 * beoordelingsstap zonder toelichtende zin, in plaats van met een verzonnen.
 */
const HR_BEOORDEELT_ZELF_TEKSTEN: Partial<Record<Verlofsoort, string>> = {
  bijzonder_verlof:
    'Dit is bijzonder verlof. Je beoordeelt het nu meteen zelf; daarna gaat het naar de loonadministratie.',
  calamiteiten_of_zorgverlof:
    'Dit is calamiteiten- of zorgverlof. Je beoordeelt het nu meteen zelf; daarmee is de aanvraag afgerond.',
};

/** §10.5 — de knop en de regel eronder, letterlijk. */
const HR_BEOORDEELT_ZELF_KNOP = 'Beoordelen en goedkeuren';
const HR_BEOORDEELT_ZELF_REGEL =
  'Je staat straks bij deze aanvraag als degene die hem invoerde én goedkeurde.';

/**
 * Story 8.1, AC-4 — het onderschrift bij een soort waarover de leidinggevende
 * niet beslist. §10.8, woord voor woord en in deze volgorde (KE-7): de tweede
 * regel staat ONDER de eerste, want samen zijn het één onderschrift.
 *
 * Ze staan hier bij de andere §10.5/§10.8-teksten en niet los in de JSX, zodat
 * met één blik na te lopen is dat er niets onderweg bedacht is. Geen van beide
 * regel noemt een boeking, een doorzetting of een wachttijd — en belooft ook
 * niet het tegenovergestelde.
 */
const ONDERSCHRIFT_ALLEEN_HR = [
  'Dit gaat niet van je verlofsaldo af. HR beoordeelt je aanvraag; je leidinggevende hoeft er niet over te beslissen.',
  'Je leidinggevende ziet meteen dat je er niet bent.',
] as const;

/**
 * Heeft een aanvraag van deze soort een HR-stap? Het antwoord komt uit
 * `VERLOFSOORTEN` (AD-27) en nooit uit een vergelijking op een soortnaam
 * (KE-9).
 *
 * TWEE HELFTEN, EN ÉÉN ERVAN IS NIET VAN DIT BESTAND (code review
 * 2026-08-17). *HR kijkt er altijd naar* is een eigen vinkje en wordt hier
 * gelezen. *HR beslist als enige* is dezelfde vraag die `goedkeuringsroute` en
 * `ALLEEN_HR_REDENEN` ook stellen; die gaat langs het gedeelde predicaat, zodat
 * er geen achtste formulering van bijkomt.
 */
function soortHeeftHrStap(verlofsoort: Verlofsoort | undefined): boolean {
  if (verlofsoort === undefined) return false;
  return VERLOFSOORTEN[verlofsoort].hrStapAltijd || leidinggevendeBeslistNiet(verlofsoort);
}

/**
 * Story 5.4: de verlofgroep waar de soortkeuze bij hoort — Nmbrs-type 3,
 * `LEAVE_TYPE_NAMES[3] = 'Kort verzuim + Bijz.verlof'` (FR-1). De andere drie
 * groepen kennen geen soort en veranderen in geen enkele stand van de
 * schakelaar.
 */
export const VERLOFGROEP_KORT_VERZUIM = 3;

const DAGDEEL_OPTIONS: { value: DayPart; label: string }[] = [
  { value: 'hele_dag', label: 'Hele dag' },
  { value: 'ochtend', label: 'Ochtend' },
  { value: 'middag', label: 'Middag' },
];

export function LeaveRequestSheet({
  isOpen,
  onClose,
  onSubmitted,
  forEmployee,
  verlofsoort,
}: LeaveRequestSheetProps) {
  const { user } = useAuth();
  const isOnBehalf = forEmployee !== undefined;
  // Story 7.5, AC-3 (AD-39): `hr`/`admin` vervullen de HR-stap in dezelfde
  // handeling; een leidinggevende niet — die stuurt de aanvraag door naar HR
  // en houdt zijn bestaande knop. Het is een uitkomstregel, geen extra poort:
  // de server beslist hetzelfde, ook als iemand het eindpunt rechtstreeks
  // aanroept.
  //
  // Story 5.2, AC-10: ÉÉN waarde draagt *de soort van deze aanvraag*. De prop
  // is er de beginwaarde van; kiest iemand in het scherm, dan wint die keuze.
  // Twee waarheden hier zou betekenen dat het scherm de ene soort toont en de
  // andere verstuurt.
  const [gekozenSoort, setGekozenSoort] = useState<Verlofsoort | undefined>(verlofsoort);
  const beoordeeltZelf =
    isOnBehalf &&
    soortHeeftHrStap(gekozenSoort) &&
    (user?.role === 'hr' || user?.role === 'admin');
  const beoordelingsZin = gekozenSoort ? (HR_BEOORDEELT_ZELF_TEKSTEN[gekozenSoort] ?? null) : null;
  /** Story 8.1, AC-4: het onderschrift verschijnt en verdwijnt met de keuze. */
  const toontOnderschrift = leidinggevendeBeslistNiet(gekozenSoort);
  // Nooit terugvallen op de eigen employeeId zodra er namens iemand wordt
  // gewerkt: dat zou stilletjes een aanvraag voor jezelf indienen.
  const employeeId = isOnBehalf ? forEmployee.id : user?.employeeId ?? undefined;
  const [confirming, setConfirming] = useState(false);
  /**
   * Gezet zodra een namens-aanvraag de server heeft bereikt — ongeacht of de
   * Nmbrs-boeking daarna lukte. Vanaf dat moment mag dit formulier NIETS meer
   * versturen: bij een mislukte of onbekend afgelopen boeking is niet vast te
   * stellen of Nmbrs de regel wél heeft ontvangen, en `bookApprovedLeaveInNmbrs`
   * doet (anders dan het retry-pad) géén dubbel-check. Nog een keer klikken zou
   * dus hetzelfde verlof een tweede keer in de loonadministratie kunnen zetten.
   */
  const [locked, setLocked] = useState(false);

  const [leaveType, setLeaveType] = useState(1);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [startPart, setStartPart] = useState<DayPart>('hele_dag');
  const [endPart, setEndPart] = useState<DayPart>('hele_dag');
  const [manualHours, setManualHours] = useState<string>('');
  const [description, setDescription] = useState('');
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  // Story 5.2, AC-6: waar of onwaar naar aanleiding van de LAATSTE wissel —
  // de zin verschijnt alleen wanneer er werkelijk iets is weggehaald.
  const [soortOpgeruimd, setSoortOpgeruimd] = useState(false);
  // Story 10.1: de gekozen gebeurtenis bij bijzonder verlof — het soort-eigen
  // veld waar story 5.2 de haak voor neerzette.
  const [gekozenGebeurtenis, setGekozenGebeurtenis] = useState<BijzondereGebeurtenis | undefined>(
    undefined
  );
  // Story 10.4: het vinkje *de datum van de begrafenis of crematie staat nog
  // niet vast*. Een eigen vlag en geen afleiding uit de gebeurtenis (AD-26).
  const [einddatumVoorlopig, setEinddatumVoorlopig] = useState(false);

  // Story 1.3 (AC-1/AC-2, AD-11): het van-tot-tijdvak. Alleen relevant, en
  // alleen ooit gevuld, wanneer isSingleDay && startPart === 'hele_dag' — zie
  // de reset-effecten hieronder.
  const [useTimes, setUseTimes] = useState(false);
  const [vanTijd, setVanTijd] = useState('');
  const [totTijd, setTotTijd] = useState('');

  const submit = useSubmitLeaveRequest();
  const { data: scheduleResp } = useEmployeeSchedule(employeeId ?? 0);
  // Feestdagen van beide jaren wanneer de periode over de jaargrens loopt —
  // anders rekent het formulier de kerst-/nieuwjaarsdagen niet mee.
  const startYear = startDate ? Number(startDate.slice(0, 4)) : new Date().getFullYear();
  const endYear = endDate ? Number(endDate.slice(0, 4)) : startYear;
  const { data: holidaysResp } = useHolidays(startYear);
  const { data: holidaysEndResp } = useHolidays(endYear);
  const { data: overviewResp } = useMijnVerlof(!isOnBehalf);
  // Story 1.1: gedeeld bedrijfsbreed pauzevenster, nodig om live uren uit een
  // tijdvak te berekenen (AC-8). `data` blijft `undefined` zolang de query
  // laadt of mislukt — het tijdenblok rekent dan simpelweg nog niet.
  const pauzeVensterQuery = usePauzeVenster();
  // Story 5.4 (AC-5): de schakelaar. `undefined` (nog aan het laden of
  // mislukt) en `null` (rij ontbreekt) betekenen hier hetzelfde: de nieuwe
  // regels gelden niet, en het formulier gedraagt zich exact zoals vandaag.
  const ingangsdatumQuery = useVerlofsoortIngangsdatum();

  // Ook resetten wanneer er een ANDERE medewerker wordt gekozen terwijl de
  // sheet openstaat: anders worden de datums en handmatige uren van de
  // vorige persoon ingediend voor de nieuwe.
  useEffect(() => {
    if (!isOpen) return;
    setLeaveType(1);
    setStartDate('');
    setEndDate('');
    setStartPart('hele_dag');
    setEndPart('hele_dag');
    setManualHours('');
    setDescription('');
    setSubmitAttempted(false);
    setServerError(null);
    setConfirming(false);
    setLocked(false);
    setUseTimes(false);
    setVanTijd('');
    setTotTijd('');
    // Terug naar de beginwaarde uit de prop, niet naar leeg: bij een
    // namens-invoer vanuit de goedkeuringslijst is dát de soort van de
    // aanvraag (story 7.5).
    setGekozenSoort(verlofsoort);
    setSoortOpgeruimd(false);
    setGekozenGebeurtenis(undefined);
    setEinddatumVoorlopig(false);
  }, [isOpen, forEmployee?.id, verlofsoort]);

  useEffect(() => {
    if (!isOpen) return;
    const listener = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submit.isPending) onClose();
    };
    document.addEventListener('keydown', listener);
    return () => document.removeEventListener('keydown', listener);
  }, [isOpen, onClose, submit.isPending]);

  const scheduleEntries = useMemo(() => {
    const schedule = scheduleResp?.data;
    if (!schedule) return [];
    // `startTime`/`endTime` genormaliseerd naar `string | null` (nooit
    // `undefined`) — Story 1.3's `findScheduleEntryForDate` verwacht die vorm.
    const weekA = schedule.weekA.map((e) => ({
      ...e,
      weekType: 'A' as const,
      startTime: e.startTime ?? null,
      endTime: e.endTime ?? null,
    }));
    const weekB = schedule.weekB.map((e) => ({
      ...e,
      weekType: 'B' as const,
      startTime: e.startTime ?? null,
      endTime: e.endTime ?? null,
    }));
    return [...weekA, ...weekB];
  }, [scheduleResp]);

  const holidayEntries = useMemo(() => {
    const start = holidaysResp?.data ?? [];
    if (startYear === endYear) return start;
    return [...start, ...(holidaysEndResp?.data ?? [])];
  }, [holidaysResp, holidaysEndResp, startYear, endYear]);

  const computedHours = useMemo(() => {
    if (!startDate || !endDate || scheduleEntries.length === 0) return null;
    if (endDate < startDate) return null;
    return calculateLeaveHours(startDate, endDate, startPart, endPart, scheduleEntries, holidayEntries);
  }, [startDate, endDate, startPart, endPart, scheduleEntries, holidayEntries]);

  /**
   * Story 10.2, AC-5 — het aantal WERKDAGEN in de gekozen periode.
   *
   * Uit de gedeelde afleiding, dezelfde die de goedkeuringslijst van HR
   * gebruikt (story 10.3, AC-2): twee tellingen die hetzelfde heten en anders
   * uitkomen zouden HR een afwijking laten zien die de medewerker niet zag.
   * Het is NIET `uren / 8` — met een halve werkdag in de periode geeft dat 4,5,
   * en dat is geen getal dat de vastgestelde zin kan dragen.
   */
  const werkdagenInPeriode = useMemo(() => {
    if (!startDate || !endDate || scheduleEntries.length === 0) return null;
    if (endDate < startDate) return null;
    return werkbareDagenInPeriode(startDate, endDate, scheduleEntries, holidayEntries);
  }, [startDate, endDate, scheduleEntries, holidayEntries]);

  /**
   * Story 5.4, AC-5 — DE voorwaarde waaronder de soortkeuze verschijnt:
   * verlofgroep 3 (FR-1) én een startdatum die onder de nieuwe regels valt.
   *
   * Deze story levert alléén deze voorwaarde. Story 5.2 hangt zijn keuzeveld
   * in de plek hieronder en hoeft aan deze regel niets te veranderen — de
   * datumvergelijking zelf staat in `packages/shared` en wordt hier niet
   * overgeschreven (KE-9, AC-4).
   *
   * Zolang de ingangsdatum niet gezet is, is de uitkomst onwaar en verschijnt
   * er niets: geen lege ruimte, geen uitgegrijsd veld en geen tekst die
   * aankondigt dat er iets aankomt.
   */
  const toontSoortkeuze =
    leaveType === VERLOFGROEP_KORT_VERZUIM &&
    startDate.length > 0 &&
    valtOnderNieuweVerlofregels(startDate, ingangsdatumQuery.data ?? null);

  /**
   * Story 5.2, AC-5 — de schermkant van de verplichting. De serverkant is
   * story 5.3 (KE-5) en dit scherm leunt er niet op.
   *
   * Bewust GEEN onderdeel van `canSubmit`: dan zou de knop uitgrijzen en zou
   * de klik nooit aankomen, waarmee de melding hieronder onbereikbaar wordt.
   * De knop blijft dus klikbaar en `handleSubmit` weigert.
   */
  const soortOntbreekt = toontSoortkeuze && gekozenSoort === undefined;
  const toonSoortFout = submitAttempted && soortOntbreekt;

  /**
   * Story 5.2, AC-6 — de velden die bij een SOORT horen en bij het wisselen
   * leeggaan. Eén plek, zodat story 10.1 zijn gebeurteniskeuze erin hangt
   * zonder de logica opnieuw te schrijven.
   *
   * Story 10.1 heeft dat gedaan: de gebeurtenis (en het vinkje dat eronder
   * hangt) zijn de soort-eigen gegevens die de PRD kent. Wisselt iemand van
   * soort terwijl er een gebeurtenis gekozen was, dan gaan ze weg én verschijnt
   * de vastgestelde zin uit §10.1 — en anders geen van beide.
   */
  const soortEigenVelden: Array<{ isGevuld: boolean; maakLeeg: () => void }> = [
    {
      isGevuld: gekozenGebeurtenis !== undefined,
      maakLeeg: () => {
        setGekozenGebeurtenis(undefined);
        setEinddatumVoorlopig(false);
      },
    },
  ];

  function kiesSoort(nieuweSoort: Verlofsoort): void {
    if (nieuweSoort === gekozenSoort) return;
    const gevuld = soortEigenVelden.filter((veld) => veld.isGevuld);
    gevuld.forEach((veld) => veld.maakLeeg());
    setSoortOpgeruimd(gevuld.length > 0);
    setGekozenSoort(nieuweSoort);
  }

  /**
   * Story 10.1, AC-4 — het VOORSTEL voor de periode, in het scherm zelf en
   * zonder wachttijd (KE-4).
   *
   * Het aantal werkdagen komt uit de gedeelde rechtentabel en de einddatum uit
   * de gedeelde `einddatumNaWerkdagen`: het handboek kent werkdagen, en vijf
   * werkdagen vanaf de dag van overlijden is zelden vijf kalenderdagen. Er gaat
   * hiervoor geen enkel verzoek naar de server — het recht komt uit `shared`.
   *
   * De BEGINdatum blijft van de medewerker: die weet wanneer de gebeurtenis
   * was, de portal niet. Staat hij nog leeg, dan valt er ook niets voor te
   * stellen en blijft het einddatumveld leeg (besluit V12: een voorstel, nooit
   * een grens — het blijft daarna gewoon aan te passen).
   *
   * De begindatum komt als ARGUMENT binnen en niet uit `startDate` (code review
   * 2026-08-17): bij het wijzigen van de startdatum draait dit voorstel opnieuw
   * binnen dezelfde afhandeling, en dan draagt de toestand nog de oude waarde.
   */
  function stelPeriodeVoor(
    gebeurtenis: BijzondereGebeurtenis,
    vanafDatum: string = startDate
  ): void {
    if (vanafDatum.length === 0 || scheduleEntries.length === 0) return;
    const regel = bijzondereGebeurtenisRegel(gebeurtenis);
    if (regel === null) return;
    setEndDate(einddatumNaWerkdagen(vanafDatum, regel.werkdagen, scheduleEntries, holidayEntries));
  }

  function kiesGebeurtenis(nieuweGebeurtenis: BijzondereGebeurtenis): void {
    if (nieuweGebeurtenis === gekozenGebeurtenis) return;
    setGekozenGebeurtenis(nieuweGebeurtenis);
    // Story 10.4: het vinkje hoort bij de VIER overlijdensgebeurtenissen. Gaat
    // de keuze naar een van de andere zes, dan is er geen begrafenisdatum meer
    // die nog niet vaststaat.
    if (!(bijzondereGebeurtenisRegel(nieuweGebeurtenis)?.isOverlijden ?? false)) {
      setEinddatumVoorlopig(false);
    }
    stelPeriodeVoor(nieuweGebeurtenis);
  }

  /**
   * Story 10.1, AC-4 — het voorstel hangt aan BEIDE gegevens (code review
   * 2026-08-17).
   *
   * Het draaide alleen bij het kiezen van een gebeurtenis. Wie de startdatum
   * daarná corrigeerde — de gebeurtenis was een week eerder dan gedacht — hield
   * de oude einddatum: de periode klapte om, de uitkomstzin verdween en de
   * indienknop grijsde uit zonder dat er iets bij stond. De correctie van de
   * startdatum is precies het moment waarop het voorstel opnieuw hoort te
   * gelden; hij blijft daarna even goed met de hand aan te passen (besluit V12).
   */
  function kiesStartdatum(nieuweStartdatum: string): void {
    setStartDate(nieuweStartdatum);
    if (gekozenGebeurtenis !== undefined) stelPeriodeVoor(gekozenGebeurtenis, nieuweStartdatum);
  }

  /**
   * Story 10.4, AC-2 — aanvinken vult het maximum uit het handboek voor, in
   * WERKdagen, vanaf de dag van overlijden. Dezelfde afleiding als het voorstel
   * hierboven; er komt geen tweede rekenregel bij (KE-9).
   */
  function zetEinddatumVoorlopig(aangevinkt: boolean): void {
    setEinddatumVoorlopig(aangevinkt);
    if (aangevinkt && gekozenGebeurtenis !== undefined) stelPeriodeVoor(gekozenGebeurtenis);
  }

  /**
   * Story 5.2, AC-7 — het voorwaardenblok, opgezocht in de microcopy-tabel en
   * nooit uit een vergelijking op een soortnaam (AD-27, KE-9).
   */
  const voorwaarden = gekozenSoort ? (VERLOFSOORT_VOORWAARDEN[gekozenSoort] ?? null) : null;

  /**
   * Story 10.1, AC-2 — de gebeurteniskeuze verschijnt ALLEEN bij bijzonder
   * verlof en bij geen enkele andere soort. Hij hangt onder dezelfde
   * voorwaarde als de soortkeuze zelf (story 5.4): zonder die keuze bestaat
   * deze niet.
   */
  const toontGebeurteniskeuze = toontSoortkeuze && isBijzonderVerlof(gekozenSoort);
  const gebeurtenisRegel =
    gekozenGebeurtenis === undefined ? null : BIJZONDER_VERLOF_GEBEURTENISSEN[gekozenGebeurtenis];
  /** Story 10.4, AC-1: het vinkje staat bij de vier overlijdens en bij de andere zes niet. */
  const toontVinkjeBegrafenisdatum =
    toontGebeurteniskeuze && (gebeurtenisRegel?.isOverlijden ?? false);

  /**
   * Story 10.4, AC-2 — de belofte onder het vinkje kán niet altijd waargemaakt
   * worden (code review 2026-08-17).
   *
   * `stelPeriodeVoor` keert zonder rooster stilzwijgend terug, terwijl de uitleg
   * *"We vullen alvast het maximum in"* er wel staat. Zonder rooster is er niets
   * te berekenen — dus wordt dat gezegd, in plaats van dat er niets gebeurt.
   * Afgeleid en niet in een toestand bijgehouden: komt het rooster alsnog
   * binnen, dan verdwijnt de melding vanzelf en vult het volgende voorstel wél.
   * Hij hangt aan het AANVINKEN — dat is de handeling waarvan de uitkomst
   * uitblijft; wie het vinkje laat staan mist niets.
   */
  const voorvullingLuktNiet =
    toontVinkjeBegrafenisdatum && einddatumVoorlopig && scheduleEntries.length === 0;

  const isSingleDay = Boolean(startDate && endDate && startDate === endDate);
  const showTijdenBlock = isSingleDay && startPart === 'hele_dag';

  // Story 1.3, taak 6: een tijd die meereist naar een aanvraag waar hij niet
  // meer geldig is (meerdaags geworden, of dagdeel niet meer 'hele_dag') zou
  // stilzwijgend verkeerd meegestuurd kunnen worden. Zodra het tijdenblok niet
  // meer zichtbaar hoort te zijn, gaat alles terug naar leeg.
  useEffect(() => {
    if (!showTijdenBlock) {
      setUseTimes(false);
      setVanTijd('');
      setTotTijd('');
    }
  }, [showTijdenBlock]);

  // Story 1.3 (AC-9/AC-10/AC-11): de roosterregel van déze ene datum, met haar
  // eigen klok-tijden — niet de vaste dagdeel-constante die `computedHours`
  // gebruikt. `undefined` zonder (matchende) roosterregel; `roosterspan` blijft
  // dan `null` (AC-11: geen referentieregel, geen clamp).
  const dayEntry = useMemo(() => {
    if (!isSingleDay || scheduleEntries.length === 0) return undefined;
    return findScheduleEntryForDate(startDate, scheduleEntries);
  }, [isSingleDay, startDate, scheduleEntries]);

  const roosterspan =
    dayEntry && dayEntry.startTime && dayEntry.endTime
      ? { start: dayEntry.startTime, end: dayEntry.endTime }
      : null;

  const timesFilled = vanTijd.length > 0 && totTijd.length > 0;

  // Story 1.3 (AC-8): live, client-side voorrekening met dezelfde gedeelde
  // resolver als de server (AD-5) — geen netwerk-aanroep, geen laadindicator.
  const tijdvakResult = useMemo(() => {
    if (!useTimes || !timesFilled || !pauzeVensterQuery.data) return null;
    return urenUitTijdvak(vanTijd, totTijd, roosterspan, pauzeVensterQuery.data);
  }, [useTimes, timesFilled, vanTijd, totTijd, roosterspan, pauzeVensterQuery.data]);

  const tijdenHours =
    tijdvakResult && !tijdvakResult.ongeldig && !tijdvakResult.volledigInPauze ? tijdvakResult.hours : null;

  // AC-8: toon de pauze-toelichting zodra het tijdvak (een deel van) de pauze
  // raakt, maar er niet volledig in valt (dat heeft al zijn eigen, blokkerende
  // melding). Alleen een presentatie-check op de al door `urenUitTijdvak`
  // geaccepteerde randen — geen herimplementatie van de urenberekening zelf.
  const pauzeOverlaps = Boolean(
    useTimes &&
      pauzeVensterQuery.data &&
      tijdvakResult &&
      !tijdvakResult.ongeldig &&
      !tijdvakResult.volledigInPauze &&
      vanTijd < pauzeVensterQuery.data.end &&
      totTijd > pauzeVensterQuery.data.start
  );

  const timesMissing = useTimes && !timesFilled;
  // AC-4: de melding hoort "bij het betreffende veld" zodra iemand ermee
  // begonnen is (één veld gevuld, het andere nog leeg) — niet meteen zodra het
  // vakje wordt aangevinkt en beide velden nog leeg zijn (dat zou hinderlijk
  // vooruitlopen zijn, vergelijkbaar met de bestaande "Kies een start- en
  // einddatum"-melding die ook pas na een poging verschijnt).
  const timesPartiallyFilled = useTimes && (vanTijd !== '' || totTijd !== '') && !timesFilled;
  const timesBlocking = useTimes && timesFilled && tijdvakResult !== null && (tijdvakResult.ongeldig || tijdvakResult.volledigInPauze);

  const isNotWorkable = computedHours !== null && computedHours === 0;
  const effectiveComputedHours = useTimes ? tijdenHours : computedHours;
  const finalHours = manualHours ? Number(manualHours) : effectiveComputedHours;
  const hasDates = startDate.length > 0 && endDate.length > 0;
  const canSubmit =
    hasDates &&
    !isNotWorkable &&
    finalHours !== null &&
    finalHours > 0 &&
    !submit.isPending &&
    !locked &&
    !timesMissing &&
    !timesBlocking;

  // Story 1.3, AC-14: alleen relevant in de tijden-flow — de melding gaat
  // expliciet over "uren berekend uit mijn tijden", niet over de generieke
  // dagdeel-berekening.
  const showHoursDeviation =
    useTimes && manualHours !== '' && tijdenHours !== null && Number(manualHours) !== tijdenHours;

  // AC 3: live saldo (Nmbrs-saldo minus eigen in_afwachting/goedgekeurd-nog-niet-synced).
  // Bij een aanvraag namens iemand anders blijft dit bewust `null`: dit is
  // het saldo van de INGELOGDE gebruiker en dus niet dat van de medewerker
  // waarvoor je invult. Beide saldo-regels hangen hieraan.
  const currentRemaining = isOnBehalf ? null : reservedRemaining(overviewResp?.data, leaveType);
  const remainingAfterRequest = currentRemaining !== null && finalHours !== null ? currentRemaining - finalHours : null;

  /**
   * Story 6.2, AC-10 — WANNEER er überhaupt naar de maandruimte gevraagd
   * wordt: alleen bij een soort die er in meetelt, in verlofgroep 3, met een
   * datum die onder de nieuwe regels valt. Bij elke andere soort en elke
   * andere verlofgroep gaat er geen verzoek uit en staat er niets — geen
   * streepje, geen lege plek die op een fout lijkt.
   *
   * `toontSoortkeuze` draagt de verlofgroep, de datum én de ingangsdatum al;
   * er komt hier geen tweede datumvergelijking naast (KE-9). Of de soort
   * meetelt komt uit het gedeelde predicaat en nooit uit een soortnaam.
   */
  const toontMaandruimte = toontSoortkeuze && soortTeltMeeInMaandruimte(gekozenSoort);
  const maandruimte = useMaandruimte({
    datum: startDate,
    // De uren die straks ook worden ingediend: de SERVER stelt daarmee vast of
    // deze aanvraag boven de grens uitkomt (KE-5). Het scherm vergelijkt niets.
    uren: finalHours,
    employeeId: forEmployee?.id,
    enabled: toontMaandruimte,
  });
  const maandruimteStand = maandruimte.data ?? null;
  /**
   * AC-8 — één toestand voor alles wat misgaat: een netwerkfout, een
   * serverfout, een verzoek dat niet op tijd antwoordt, én een server die
   * zegt dat er geen stand is (de ingangsdatum ontbreekt of is onleesbaar).
   * Ze leveren allemaal dezelfde zin, en de indienknop blijft in alle gevallen
   * aan — `canSubmit` hierboven kent deze query niet en dat is de bedoeling.
   */
  const maandruimteNietOpTeHalen =
    maandruimte.isError || (maandruimte.isSuccess && maandruimte.data == null);
  const toontMaandruimteMelding =
    toontMaandruimte && (maandruimteStand !== null || maandruimteNietOpTeHalen);

  if (!isOpen) return null;

  async function handleSubmit() {
    setSubmitAttempted(true);
    setServerError(null);
    // Story 5.2, AC-5: zonder soort gaat er niets weg. Apart van `canSubmit`
    // omdat de knop klikbaar moet blijven — zie `soortOntbreekt`.
    if (!canSubmit || soortOntbreekt) return;

    // Namens iemand anders is onomkeerbaar: direct goedgekeurd én meteen in
    // de loonadministratie geboekt, en intrekken bestaat nog niet. Dus
    // eerst laten zien wát er precies gaat gebeuren.
    if (isOnBehalf && !confirming) {
      setConfirming(true);
      return;
    }

    try {
      const result = await submit.mutateAsync({
        leaveType,
        startDate,
        endDate,
        startPart,
        endPart: isSingleDay ? startPart : endPart,
        hours: manualHours ? Number(manualHours) : undefined,
        description: description || undefined,
        ...(useTimes && timesFilled ? { startTime: vanTijd, endTime: totTijd } : {}),
        ...(isOnBehalf ? { employeeId: forEmployee.id } : {}),
        // Story 7.5: de soort gaat mee, want de server leidt er de HR-stap uit
        // af. Zonder dit veld blijft `hr_stap_reden` leeg en is de hele stap
        // met één ontbrekende regel uitgeschakeld. Story 5.2: dezelfde ene
        // waarde die het scherm toont — langs het bestaande veld, geen nieuwe
        // route (AC-11).
        ...(gekozenSoort ? { verlofsoort: gekozenSoort } : {}),
        // Story 10.1, AC-7: de gekozen gebeurtenis gaat mee langs hetzelfde
        // veld en dezelfde route — geen nieuw eindpunt. Story 10.4, AC-3: het
        // vinkje is een EIGEN vlag en geen afleiding uit de gebeurtenis; hij
        // reist alleen mee wanneer hij werkelijk is aangevinkt.
        ...(gekozenGebeurtenis ? { bijzondereGebeurtenis: gekozenGebeurtenis } : {}),
        ...(einddatumVoorlopig ? { einddatumVoorlopig: true } : {}),
      });
      // Opgeslagen is niet hetzelfde als geboekt. Bij een mislukte
      // Nmbrs-boeking staat het verlof wél goedgekeurd — dat moet je weten,
      // anders denk je dat alles rond is terwijl de loonadministratie niets
      // heeft ontvangen.
      // Alles behalve een bevestigde `synced` betekent: de aanvraag staat
      // goedgekeurd, maar of Nmbrs hem heeft is onzeker. Sheet blijft open
      // met uitleg, en op slot — opnieuw versturen zou dubbel kunnen boeken.
      if (isOnBehalf && result?.data?.syncStatus !== 'synced') {
        setConfirming(false);
        setLocked(true);
        setServerError(
          'Het verlof is vastgelegd en goedgekeurd, maar de boeking in Nmbrs is niet bevestigd. Verstuur dit formulier NIET opnieuw — controleer de aanvraag in de goedkeuringslijst en gebruik daar "Opnieuw synchroniseren".'
        );
        return;
      }
      onSubmitted?.();
      onClose();
    } catch (err) {
      setConfirming(false);
      // Bij een namens-aanvraag kan een netwerk-/serverfout ná de commit zijn
      // opgetreden. Dan is niet te zien of er al iets is vastgelegd, dus ook
      // hier: op slot in plaats van uitnodigen tot opnieuw proberen.
      if (isOnBehalf) setLocked(true);
      if (err instanceof ApiRequestError) {
        setServerError(err.message);
      } else {
        setServerError('Aanvraag indienen mislukt. Probeer het opnieuw.');
      }
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/30" data-testid="leave-request-sheet-overlay">
      <div className="w-full max-w-md h-full bg-white p-6 overflow-y-auto shadow-xl" data-testid="leave-request-sheet">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-bold text-[#1E293B]">
            {isOnBehalf ? `Verlof invoeren voor ${forEmployee.name}` : 'Verlof aanvragen'}
          </h2>
          <button type="button" onClick={onClose} aria-label="Sluiten" className="text-gray-400 hover:text-gray-600">
            ✕
          </button>
        </div>

        {serverError && (
          <div data-testid="leave-request-server-error" className="mb-4 px-3 py-2 rounded-md bg-[#D64545]/10 text-[#D64545] text-sm">
            {serverError}
          </div>
        )}

        <div className="mb-4">
          <label htmlFor="leave-type-select" className="block text-sm font-medium text-gray-700 mb-1">Verloftype</label>
          <select
            id="leave-type-select"
            value={leaveType}
            onChange={(e) => setLeaveType(Number(e.target.value))}
            className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm"
          >
            {Object.entries(LEAVE_TYPE_NAMES).map(([id, name]) => (
              <option key={id} value={id}>{name}</option>
            ))}
          </select>
          {currentRemaining !== null && (
            <p className="mt-1 text-xs text-[#54949E]" data-testid="leave-current-remaining">
              Huidig resterend saldo: {currentRemaining} uur
            </p>
          )}
        </div>

        {/* Story 5.4, AC-5: de plek waar de soortkeuze komt te staan. Deze
            story levert de voorwaarde, story 5.2 het veld — die hangt zijn
            keuzelijst hierin en raakt de conditie hierboven niet aan.
            Zolang de schakelaar uit staat wordt hier NIETS gerenderd: geen
            lege ruimte, geen uitgegrijsd veld, geen aankondiging.

            Story 5.2 zet de marge er samen met zijn veld in — de plaatshouder
            droeg er bewust geen (code review 2026-08-16), omdat een lege `div`
            met `mb-4` zestien pixels onverklaard gat boven de datumvelden
            oplevert. Nu er inhoud staat, hoort de marge er wél. */}
        {toontSoortkeuze && (
          <fieldset
            data-testid="verlofsoort-keuze"
            role="radiogroup"
            aria-labelledby="verlofsoort-vraag"
            // Story 8.1, AC-9: het onderschrift hangt aan het VELD waar het over
            // gaat en staat er niet alleen visueel onder (KE-3). Beide
            // beschrijvingen reizen mee zodra ze er zijn; een schermlezer leest
            // ze bij het soortveld voor.
            aria-describedby={
              [
                toonSoortFout ? 'verlofsoort-fout' : null,
                toontOnderschrift ? 'verlofsoort-onderschrift' : null,
              ]
                .filter(Boolean)
                .join(' ') || undefined
            }
            className="mb-4"
          >
            <legend id="verlofsoort-vraag" className="block text-sm font-medium text-gray-700 mb-2">
              {VERLOFSOORT_VRAAG}
            </legend>

            <div className="space-y-2">
              {(Object.keys(VERLOFSOORTEN) as Verlofsoort[]).map((soort) => {
                const tekst = VERLOFSOORT_KEUZE_TEKSTEN[soort];
                const uitlegId = `verlofsoort-uitleg-${soort}`;
                return (
                  <label key={soort} htmlFor={`verlofsoort-${soort}`} className="flex gap-2 items-start cursor-pointer">
                    <input
                      id={`verlofsoort-${soort}`}
                      type="radio"
                      name="verlofsoort"
                      value={soort}
                      checked={gekozenSoort === soort}
                      onChange={() => kiesSoort(soort)}
                      aria-describedby={
                        toonSoortFout ? `${uitlegId} verlofsoort-fout` : uitlegId
                      }
                      className="mt-1 h-4 w-4"
                    />
                    <span className="text-sm text-[#1E293B]">
                      <span className="block font-medium">{tekst.label}</span>
                      <span id={uitlegId} className="block text-xs text-gray-600">
                        <span className="block">{tekst.uitleg}</span>
                        {tekst.wegwijs && <span className="block mt-1">{tekst.wegwijs}</span>}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>

            {/* Story 8.1, AC-4 (§10.8): het onderschrift bij een soort waarover
                de leidinggevende niet beslist. Tekst en niets anders — geen
                knop, geen link, geen focusstop (AC-9), en geen waarschuwingsvorm:
                dit is geen fout maar een mededeling. */}
            {toontOnderschrift && (
              <div
                id="verlofsoort-onderschrift"
                data-testid="verlofsoort-onderschrift"
                className="mt-2 text-xs text-gray-600"
              >
                {ONDERSCHRIFT_ALLEEN_HR.map((regel) => (
                  <span key={regel} className="block mt-1 first:mt-0">
                    {regel}
                  </span>
                ))}
              </div>
            )}

            {toonSoortFout && (
              <p id="verlofsoort-fout" data-testid="verlofsoort-fout" className="mt-2 text-xs text-[#D64545]">
                {VERLOFSOORT_FOUT_GEEN_KEUZE}
              </p>
            )}

            {/* AC-6, taak 7: waarneembaar met een schermlezer en niet alleen
                visueel. Leeg zolang er niets is weggehaald — de zin wrijft
                iemand anders iets aan wat niet gebeurd is. */}
            <p role="status" aria-live="polite" className="mt-2 text-xs text-[#54949E]">
              {soortOpgeruimd ? VERLOFSOORT_OPGERUIMD : ''}
            </p>

            {voorwaarden && (
              <section
                role="region"
                aria-labelledby="verlofsoort-voorwaarden-kop"
                data-testid="verlofsoort-voorwaarden"
                className="mt-3 px-3 py-3 rounded-md border border-[#54949E]/40 bg-[#54949E]/5"
              >
                <h3 id="verlofsoort-voorwaarden-kop" className="text-sm font-medium text-[#1E293B]">
                  {voorwaarden.kop}
                </h3>
                <ul className="mt-2 space-y-1 text-xs text-gray-600">
                  {voorwaarden.regels.map((regel) => (
                    <li key={regel}>{regel}</li>
                  ))}
                </ul>
              </section>
            )}
          </fieldset>
        )}

        {/* ── Story 10.1: de gebeurteniskeuze bij bijzonder verlof (§10.3) ──

            EEN EIGEN VELD NAAST DE SOORTKEUZE, niet erin: de twee vragen zijn
            twee vragen, en een schermlezer hoort ze los van elkaar.

            GEEN `aria-live` OM DE TIEN OPTIES (code review 2026-08-17). Die
            stond hier, om de hele fieldset — en juist die tien teksten
            veranderen nooit. Wat wél verandert is de EINDDATUM, en dat veld
            staat buiten deze fieldset; een schermlezer kondigde de wijziging dus
            aan op de enige plek waar niets wijzigde. De aankondiging hangt nu
            aan de uitkomstzin verderop, die de nieuwe periode letterlijk draagt
            (KE-3). De tien teksten komen woord voor woord uit de gedeelde
            rechtentabel en worden hier niet overgeschreven (AD-38). */}
        {toontGebeurteniskeuze && (
          <fieldset
            data-testid="bijzondere-gebeurtenis-keuze"
            role="radiogroup"
            aria-labelledby="bijzondere-gebeurtenis-vraag"
            className="mb-4"
          >
            <legend
              id="bijzondere-gebeurtenis-vraag"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              {GEBEURTENIS_VRAAG}
            </legend>

            <div className="space-y-2">
              {BIJZONDER_VERLOF_GEBEURTENIS_WAARDEN.map((sleutel) => (
                <label
                  key={sleutel}
                  htmlFor={`bijzondere-gebeurtenis-${sleutel}`}
                  className="flex gap-2 items-start cursor-pointer"
                >
                  <input
                    id={`bijzondere-gebeurtenis-${sleutel}`}
                    type="radio"
                    name="bijzondere-gebeurtenis"
                    value={sleutel}
                    checked={gekozenGebeurtenis === sleutel}
                    onChange={() => kiesGebeurtenis(sleutel)}
                    className="mt-1 h-4 w-4"
                  />
                  {/* KE-3: de gebeurtenis draagt haar betekenis als TEKST — niet
                      als code, tint of pictogram. De machinesleutel staat
                      nergens leesbaar op het scherm. */}
                  <span className="text-sm text-[#1E293B]">
                    {BIJZONDER_VERLOF_GEBEURTENISSEN[sleutel].tekst}
                  </span>
                </label>
              ))}
            </div>

            {/* AC-4: de toelichting bij het voorgevulde voorstel. Geen
                waarschuwingsvorm — afwijken van het voorstel mag, en is geen
                fout (besluit V12). */}
            {gekozenGebeurtenis !== undefined && (
              <p data-testid="bijzonder-verlof-voorstel" className="mt-3 text-xs text-gray-600">
                {VOORSTEL_TOELICHTING}
              </p>
            )}

            {/* ── Story 10.4, AC-1: het vinkje bij de vier overlijdens ──
                Of het een overlijden betreft komt uit de gedeelde rechtentabel
                en niet uit een lijst van vier sleutels hier (KE-9). */}
            {toontVinkjeBegrafenisdatum && (
              <div className="mt-3" data-testid="einddatum-voorlopig-blok">
                <label className="flex gap-2 items-start cursor-pointer text-sm text-[#1E293B]">
                  <input
                    type="checkbox"
                    checked={einddatumVoorlopig}
                    onChange={(e) => zetEinddatumVoorlopig(e.target.checked)}
                    className="mt-1 h-4 w-4"
                  />
                  {VINKJE_BEGRAFENISDATUM}
                </label>
                <p className="mt-1 text-xs text-gray-600">{VINKJE_UITLEG}</p>
                {/* De belofte hierboven kán niet altijd waargemaakt worden.
                    Dezelfde vorm als de maandruimte-melding van story 6.2:
                    informatief, `role="status"`, geen foutkleur, en de
                    indienknop blijft aan. */}
                {voorvullingLuktNiet && (
                  <p
                    data-testid="voorvulling-mislukt"
                    role="status"
                    aria-live="polite"
                    className="mt-1 text-xs text-[#1E293B]"
                  >
                    {VOORVULLING_MISLUKT}
                  </p>
                )}
              </div>
            )}

            {/* AC-6: de twee aanvullende bepalingen bij de REGELING — ze staan
                er onafhankelijk van welke gebeurtenis gekozen is, en zonder
                verzonnen kopje erboven (KE-7). */}
            <div className="mt-3 text-xs text-gray-600" data-testid="bijzonder-verlof-bepalingen">
              {AANVULLENDE_BEPALINGEN.map((bepaling) => (
                <span key={bepaling} className="block mt-1 first:mt-0">
                  {bepaling}
                </span>
              ))}
            </div>

            {/* AC-8: bijzonder verlof gaat altijd langs HR, hoeveel dagen het
                ook betreft — ook wanneer de aanvraag precies gelijk is aan het
                voorstel (besluit V5). */}
            <p className="mt-2 text-xs text-gray-600" data-testid="bijzonder-verlof-altijd-hr">
              {ALTIJD_LANGS_HR}
            </p>
          </fieldset>
        )}

        {/* Story 6.2 (FR-5, §10.2): hoeveel van de drie uur er deze maand nog
            over is. INFORMATIEF en nooit blokkerend (KE-4): de knop blijft aan,
            ook boven de grens en ook wanneer het getal er niet is.

            Geen waarschuwingsvorm (KE-3): dit is geen fout, dus geen rood, geen
            `role="alert"` en geen pictogram. `aria-live` zorgt dat een wijziging
            — een andere datum, andere uren — ook wordt aangekondigd voor wie het
            scherm niet ziet. Het is tekst en geen knop: niets in de
            tab-volgorde. */}
        {toontMaandruimteMelding && (
          <div
            data-testid="maandruimte-melding"
            role="status"
            aria-live="polite"
            className="mb-4 px-3 py-3 rounded-md border border-[#54949E]/30 bg-[#54949E]/5 text-xs text-[#1E293B] space-y-1"
          >
            {maandruimteStand !== null ? (
              <>
                <p>{maandruimteZin(maandruimteStand)}</p>
                <p className="text-gray-600">{MAANDRUIMTE_TOELICHTING_GRENS}</p>
                <p className="text-gray-600">{MAANDRUIMTE_TOELICHTING_DEELTIJD}</p>
              </>
            ) : (
              <p>{MAANDRUIMTE_NIET_OP_TE_HALEN}</p>
            )}
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 mb-4">
          <div>
            <label htmlFor="leave-start-date" className="block text-sm font-medium text-gray-700 mb-1">Startdatum</label>
            <input
              id="leave-start-date"
              type="date"
              value={startDate}
              onChange={(e) => kiesStartdatum(e.target.value)}
              className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm"
            />
          </div>
          <div>
            <label htmlFor="leave-end-date" className="block text-sm font-medium text-gray-700 mb-1">Einddatum</label>
            <input
              id="leave-end-date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-4">
          <div>
            <label htmlFor="leave-start-part" className="block text-sm font-medium text-gray-700 mb-1">
              {isSingleDay ? 'Dagdeel' : 'Dagdeel (start)'}
            </label>
            <select
              id="leave-start-part"
              value={startPart}
              onChange={(e) => setStartPart(e.target.value as DayPart)}
              className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm"
            >
              {DAGDEEL_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          {!isSingleDay && (
            <div>
              <label htmlFor="leave-end-part" className="block text-sm font-medium text-gray-700 mb-1">Dagdeel (eind)</label>
              <select
                id="leave-end-part"
                value={endPart}
                onChange={(e) => setEndPart(e.target.value as DayPart)}
                className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm"
              >
                {DAGDEEL_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          )}
        </div>

        {showTijdenBlock && (
          <div className="mb-4" data-testid="kort-verlof-tijdvak">
            <label className="flex items-center gap-2 text-sm text-gray-700 mb-2">
              <input
                type="checkbox"
                checked={useTimes}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setUseTimes(checked);
                  if (!checked) {
                    setVanTijd('');
                    setTotTijd('');
                  }
                }}
                className="h-4 w-4"
              />
              Ik ben maar een deel van de dag weg
            </label>

            {useTimes && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label htmlFor="leave-van-tijd" className="block text-xs text-gray-500 mb-1">
                      Van
                    </label>
                    <input
                      id="leave-van-tijd"
                      type="time"
                      step={300}
                      value={vanTijd}
                      onChange={(e) => setVanTijd(e.target.value)}
                      aria-describedby="leave-tijden-error"
                      className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm"
                    />
                  </div>
                  <div>
                    <label htmlFor="leave-tot-tijd" className="block text-xs text-gray-500 mb-1">
                      Tot
                    </label>
                    <input
                      id="leave-tot-tijd"
                      type="time"
                      step={300}
                      value={totTijd}
                      onChange={(e) => setTotTijd(e.target.value)}
                      aria-describedby="leave-tijden-error"
                      className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm"
                    />
                  </div>
                </div>

                {timesPartiallyFilled && (
                  <p id="leave-tijden-error" data-testid="leave-tijden-missing-error" className="mt-1 text-xs text-[#D64545]">
                    Vul een begin- en eindtijd in.
                  </p>
                )}
                {!timesPartiallyFilled && tijdvakResult?.ongeldig && (
                  <p id="leave-tijden-error" data-testid="leave-tijden-order-error" className="mt-1 text-xs text-[#D64545]">
                    De eindtijd moet later zijn dan de begintijd.
                  </p>
                )}
                {!timesMissing && tijdvakResult && !tijdvakResult.ongeldig && tijdvakResult.volledigInPauze && (
                  <p id="leave-tijden-error" data-testid="leave-tijden-pauze-error" className="mt-1 text-xs text-[#D64545]">
                    Dit valt helemaal in de pauze — er zijn dan geen verlofuren.
                  </p>
                )}
                {tijdenHours !== null && (
                  <p className="mt-1 text-sm text-gray-700" data-testid="kort-verlof-tijdvak-uren">
                    {formatNlUren(tijdenHours)} uur
                    {pauzeOverlaps && pauzeVensterQuery.data
                      ? ` — pauze ${pauzeVensterQuery.data.start}–${pauzeVensterQuery.data.end} telt niet mee`
                      : ''}
                    {tijdvakResult?.afgerond ? ' (afgerond op een kwartier)' : ''}
                  </p>
                )}
                {tijdvakResult?.buitenWerkdag && roosterspan && (
                  <p role="alert" data-testid="kort-verlof-buiten-werkdag" className="mt-1 text-xs text-[#D64545]">
                    {`Je werkdag loopt van ${roosterspan.start} tot ${roosterspan.end}. Alleen de uren daarbinnen tellen mee.`}
                  </p>
                )}
                {roosterspan && (
                  <p className="mt-1 text-xs text-gray-500" data-testid="kort-verlof-referentieregel">
                    {`Je werkdag loopt van ${roosterspan.start} tot ${roosterspan.end}`}
                  </p>
                )}
              </>
            )}
          </div>
        )}

        <div className="mb-4">
          {isNotWorkable ? (
            <p data-testid="leave-request-not-workable" className="text-sm text-[#D64545]">
              Deze aanvraag valt volledig op niet-werkbare dagdelen.
            </p>
          ) : !useTimes && computedHours !== null ? (
            <p className="text-sm text-gray-700">
              Berekende uren: <span className="font-semibold">{computedHours} uur</span>
            </p>
          ) : null}
          {remainingAfterRequest !== null && !isNotWorkable && (
            <p
              data-testid="leave-remaining-after"
              className={`mt-1 text-sm font-medium ${remainingAfterRequest >= 0 ? 'text-[#5C7A00]' : 'text-[#D64545]'}`}
            >
              Resterend na deze aanvraag: {remainingAfterRequest} uur
            </p>
          )}
          <label htmlFor="leave-hours-override" className="block text-xs text-gray-500 mt-2 mb-1">
            Uren aanpassen (optioneel)
          </label>
          <input
            id="leave-hours-override"
            type="number"
            min={0}
            step={0.5}
            value={manualHours}
            onChange={(e) => setManualHours(e.target.value)}
            className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm"
          />
          {showHoursDeviation && tijdenHours !== null && (
            <p data-testid="leave-hours-deviation" className="mt-1 text-xs text-[#54949E]">
              {`Uit je tijden komt ${formatNlUren(tijdenHours)} uur. Je vult zelf ${formatNlUren(Number(manualHours))} uur in.`}
            </p>
          )}

          {/* ── Story 10.2, AC-5: de uitkomst van de periode in woorden (§10.3) ──

              GEEN MELDING DIE DE MEDEWERKER EEN FOUT AANWRIJFT (FR-13). Levert
              de periode minder uren op dan hij verwachtte, dan staat er neutraal
              bij hoeveel eruit volgt en waarom: geen rood, geen `role="alert"`,
              geen bevestigingsvraag.

              Bij bijzonder verlof en daar alleen: §10.3 is de sectie over deze
              soort, bij medisch bezoek gaat het om uren binnen één dag en is de
              zin onzinnig.

              `aria-live` zodat een wisseling van de periode wordt aangekondigd
              en niet alleen zichtbaar is (KE-3). De samenloop-uitleg is gewone
              tekst in de documentstroom en geen tooltip — die is met het
              toetsenbord niet betrouwbaar te bereiken. */}
          {isBijzonderVerlof(gekozenSoort) &&
            computedHours !== null &&
            werkdagenInPeriode !== null && (
              <div
                data-testid="bijzonder-verlof-uitkomst"
                aria-live="polite"
                className="mt-2 text-sm text-gray-700"
              >
                <p>
                  {uitkomstZin(
                    startDate,
                    endDate,
                    werkdagenInPeriode,
                    formatNlUren(computedHours)
                  )}
                </p>
                <p className="mt-1 text-xs text-gray-600">{SAMENLOOP_UITLEG}</p>
              </div>
            )}
        </div>

        <div className="mb-6">
          <label htmlFor="leave-description" className="block text-sm font-medium text-gray-700 mb-1">Toelichting (optioneel)</label>
          <textarea
            id="leave-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm"
          />
        </div>

        {submitAttempted && !hasDates && (
          <p className="mb-4 text-xs text-gray-500">Kies een start- en einddatum.</p>
        )}

        {forEmployee && hasDates && scheduleEntries.length === 0 && (
          <p data-testid="leave-no-schedule" className="mb-4 text-xs text-[#D64545]">
            {/* Handmatige uren helpen hier NIET: de server toetst werkbaarheid
                op het berekende rooster en weigert met LEAVE_NOT_WORKABLE,
                ongeacht wat je invult. Dus alleen het echte advies geven. */}
            Geen werkrooster bekend voor {forEmployee.name}. Leg eerst het werkrooster vast — zonder rooster kan er geen verlof worden geboekt.
          </p>
        )}

        {confirming && forEmployee && (
          <div
            data-testid="leave-onbehalf-confirm"
            className="mb-4 px-3 py-3 rounded-md border border-[#2F5A7A]/30 bg-[#2F5A7A]/5 text-sm text-[#1E293B]"
          >
            <p className="font-medium mb-2">Controleer voordat je vastlegt</p>
            <ul className="space-y-0.5 text-xs">
              <li>Medewerker: <strong>{forEmployee.name}</strong></li>
              <li>Periode: <strong>{startDate}{endDate !== startDate ? ` t/m ${endDate}` : ''}</strong></li>
              <li>Uren: <strong>{finalHours}</strong></li>
              <li>Verloftype: <strong>{LEAVE_TYPE_NAMES[leaveType] ?? leaveType}</strong></li>
            </ul>
            {/* Story 7.5, AC-3: bij een aanvraag die HR in dit scherm zelf
                beoordeelt klopt deze zin niet — bij een niet-boekende soort
                gaat er niets naar de loonadministratie (FR-21/FR-23), en bij
                bijzonder verlof zegt de §10.5-zin hieronder al wat er gebeurt. */}
            {!beoordeeltZelf && (
              <p className="mt-2 text-xs text-gray-600">
                Dit wordt meteen goedgekeurd en in Nmbrs geboekt. Terugdraaien kan alleen handmatig
                in Nmbrs.
              </p>
            )}
          </div>
        )}

        {/* Story 7.5, AC-3/AC-11 (besluit V24): de beoordeling is een EIGEN
            handeling, los van het opslaan — en een `region` met een
            toegankelijke naam, geen vetgedrukte regel (KE-3). */}
        {confirming && beoordeeltZelf && (
          <section
            role="region"
            aria-label="Beoordelen en goedkeuren"
            data-testid="leave-onbehalf-hr-beoordeling"
            className="mb-4 px-3 py-3 rounded-md border border-[#54949E]/40 bg-[#54949E]/5 text-sm text-[#1E293B]"
          >
            {beoordelingsZin && <p>{beoordelingsZin}</p>}
            <p id="leave-onbehalf-hr-gevolg" className="mt-2 text-xs text-gray-600">
              {HR_BEOORDEELT_ZELF_REGEL}
            </p>
          </section>
        )}

        {locked ? (
          <button
            type="button"
            data-testid="leave-onbehalf-close"
            onClick={onClose}
            className="w-full px-4 py-2 rounded-md border border-[#E2E8F0] text-sm font-medium text-gray-700"
          >
            Sluiten
          </button>
        ) : (
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            // AC-11: de regel onder de knop draagt zijn betekenis als tekst en
            // hangt via `aria-describedby` aan de handeling waar hij over gaat.
            aria-describedby={
              confirming && beoordeeltZelf ? 'leave-onbehalf-hr-gevolg' : undefined
            }
            className="w-full px-4 py-2 rounded-md bg-[#2F5A7A] text-white text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {!isOnBehalf
              ? 'Aanvraag indienen'
              : confirming
                ? beoordeeltZelf
                  ? HR_BEOORDEELT_ZELF_KNOP
                  : 'Definitief vastleggen'
                : 'Controleren en vastleggen'}
          </button>
        )}

        {confirming && !locked && (
          <button
            type="button"
            onClick={() => setConfirming(false)}
            className="w-full mt-2 px-4 py-2 rounded-md border border-[#E2E8F0] text-sm text-gray-600"
          >
            Terug
          </button>
        )}
      </div>
    </div>
  );
}
